import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { productService } from '../lib/firebaseService';
import { Link } from 'react-router-dom';
import {
    DollarSign, Package, Calendar, TrendingDown,
    ArrowRight, Plus, Upload, CheckCircle, TrendingUp
} from 'lucide-react';
import '../styles/dashboard.css';
import { useAuth } from '../context/AuthContext';
import { t } from '../utils/translation';

export default function Dashboard() {
    const { currentUser } = useAuth();
    const { data: stats, isLoading } = useQuery({ queryKey: ['dashboard'], queryFn: productService.getDashboardStats });

    if (isLoading) return <div className="p-8">{t('COMMON.LOADING')}</div>;

    // Calculate days until upcoming event
    const daysUntil = stats?.upcomingEvent
        ? Math.ceil((new Date(stats.upcomingEvent.date) - new Date()) / (1000 * 60 * 60 * 24))
        : null;

    return (
        <div className="dashboard-container">
            <div className="dashboard-header-row">
                <div>
                    <h1 className="welcome-text">{t('DASHBOARD.TITLE')}</h1>
                    <p className="welcome-subtext">{t('DASHBOARD.SUBTITLE')}</p>
                </div>
                <div className="flex gap-3">
                    <Link to="/imports" className="btn btn-secondary bg-white">
                        <Upload size={16} className="mr-2" /> {t('NAV.SALES_IMPORT')}
                    </Link>
                    <Link to="/products" className="btn btn-primary">
                        <Plus size={16} className="mr-2" /> {t('PRODUCTS.ADD_PRODUCT')}
                    </Link>
                </div>
            </div>

            <div className="quick-actions-grid">
                <Link to="/inventory" className="action-card">
                    <div className="flex items-center">
                        <div className="action-icon-box" style={{ backgroundColor: '#F3E8FF', color: '#6C2BFF' }}>
                            <Package size={20} />
                        </div>
                        <div className="action-info">
                            <h4>{t('NAV.INVENTORY')}</h4>
                            <p>{stats?.totalStockValue ? `€${(stats.totalStockValue / 1000).toFixed(1)}k ${t('PRODUCTS.TABLE.VALUE')}` : t('DASHBOARD.VIEW_ALERTS')}</p>
                        </div>
                    </div>
                    <ArrowRight size={16} className="arrow-icon" />
                </Link>

                <Link to="/events" className="action-card">
                    <div className="flex items-center">
                        <div className="action-icon-box" style={{ backgroundColor: '#DBEAFE', color: '#2563EB' }}>
                            <Calendar size={20} />
                        </div>
                        <div className="action-info">
                            <h4>{t('NAV.EVENTS')}</h4>
                            <p>{stats?.completedEvents || 0} {t('EVENTS.STATUS.COMPLETED')}</p>
                        </div>
                    </div>
                    <ArrowRight size={16} className="arrow-icon" />
                </Link>

                <Link to="/reports" className="action-card">
                    <div className="flex items-center">
                        <div className="action-icon-box" style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}>
                            <DollarSign size={20} />
                        </div>
                        <div className="action-info">
                            <h4>{t('NAV.REPORTS')}</h4>
                            <p>{t('COMMON.EXPORT')}</p>
                        </div>
                    </div>
                    <ArrowRight size={16} className="arrow-icon" />
                </Link>
            </div>

            <div className="dashboard-grid">
                <div className="flex flex-col gap-6">
                    {/* Last Event Revenue - Clickable */}
                    {stats?.lastEventRevenue ? (
                        <Link
                            to={`/events/${stats.lastEventRevenue.eventId}`}
                            className="hero-card cursor-pointer hover:scale-[1.01] transition-transform"
                            style={{ textDecoration: 'none' }}
                        >
                            <div className="hero-header">
                                <div>
                                    <h3 className="hero-title">{t('DASHBOARD.LAST_EVENT')}</h3>
                                    <div className="text-lg font-medium mt-2 text-gray-300">
                                        {stats.lastEventRevenue.name}
                                    </div>
                                    <div style={{ fontSize: '28px', fontWeight: '700', marginTop: '4px' }}>
                                        €{stats.lastEventRevenue.revenue.toLocaleString()}
                                    </div>
                                    <div className="text-sm text-gray-400 mt-2">
                                        {new Date(stats.lastEventRevenue.date).toLocaleDateString('fr-FR', {
                                            weekday: 'short', day: 'numeric', month: 'short'
                                        })} • {stats.lastEventRevenue.transactionCount} ventes
                                    </div>
                                </div>
                                <ArrowRight size={20} className="text-gray-500" />
                            </div>
                        </Link>
                    ) : (
                        <div className="hero-card">
                            <div className="hero-header">
                                <div>
                                    <h3 className="hero-title">{t('DASHBOARD.LAST_EVENT')}</h3>
                                    <div className="text-gray-400 mt-4">{t('DASHBOARD.NO_EVENT_REVENUE')}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Top Products of the Month */}
                    <div className="list-card flex-1">
                        <div className="list-header">
                            <h3 className="list-title">{t('DASHBOARD.TOP_PRODUCTS')}</h3>
                        </div>
                        <div className="space-y-2">
                            {stats?.topProducts && stats.topProducts.length > 0 ? (
                                stats.topProducts.map((product, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="text-lg font-bold text-gray-400">#{idx + 1}</div>
                                            <div>
                                                <div className="font-medium">{product.name}</div>
                                                <div className="text-sm text-gray-500">{product.qty} {t('DASHBOARD.UNITS')}</div>
                                            </div>
                                        </div>
                                        <div className="text-lg font-bold text-purple-600">
                                            €{product.revenue.toLocaleString()}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 text-gray-400 text-sm">
                                    Aucune vente ce mois
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-6">
                    {/* KPI Strip */}
                    <div className="kpi-strip">
                        <div className="kpi-card-mini">
                            <span className="kpi-label">{t('DASHBOARD.YEAR_REVENUE')} {new Date().getFullYear()}</span>
                            <span className="kpi-value">€{stats?.yearRevenue?.toLocaleString() || 0}</span>
                            {stats?.yearRevenue > 0 && (
                                <span className="kpi-trend trend-up">
                                    <TrendingUp size={12} className="mr-1" />
                                    Année en cours
                                </span>
                            )}
                        </div>
                        <div className="kpi-card-mini">
                            <span className="kpi-label">{t('DASHBOARD.AVG_BASKET')}</span>
                            <span className="kpi-value">€{stats?.avgBasket?.toFixed(2) || '0.00'}</span>
                            <span className="text-xs text-gray-500">par transaction</span>
                        </div>
                        <div className="kpi-card-mini">
                            <span className="kpi-label">{t('DASHBOARD.LOW_STOCK')}</span>
                            <span className="kpi-value">{stats?.lowStock || 0}</span>
                            <span className={`kpi-trend ${stats?.lowStock > 0 ? 'trend-down' : 'trend-up'}`}>
                                {stats?.lowStock > 0 ? <TrendingDown size={12} className="mr-1" /> : <CheckCircle size={12} className="mr-1" />}
                                {stats?.lowStock > 0 ? t('DASHBOARD.ITEMS_NEED_RESTOCK') : 'OK'}
                            </span>
                        </div>
                    </div>

                    {/* Upcoming Event */}
                    {stats?.upcomingEvent ? (
                        <Link
                            to={`/events/${stats.upcomingEvent.id}`}
                            className="list-card flex-1 cursor-pointer hover:shadow-lg transition-shadow"
                            style={{ textDecoration: 'none' }}
                        >
                            <div className="list-header">
                                <h3 className="list-title">{t('DASHBOARD.UPCOMING_EVENT')}</h3>
                                <ArrowRight size={16} className="text-gray-400" />
                            </div>
                            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                                <div className="text-xl font-bold text-gray-900">{stats.upcomingEvent.name}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                    {new Date(stats.upcomingEvent.date).toLocaleDateString('fr-FR', {
                                        weekday: 'long', day: 'numeric', month: 'long'
                                    })}
                                </div>
                                <div className="flex items-center gap-4 mt-3">
                                    <div className="text-sm text-gray-500">
                                        {stats.upcomingEvent.expectedGuests || 0} {t('DASHBOARD.GUESTS')}
                                    </div>
                                    <div className="text-sm font-bold text-purple-600">
                                        {t('DASHBOARD.DAYS_UNTIL')}{daysUntil}
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ) : (
                        <div className="list-card flex-1">
                            <div className="list-header">
                                <h3 className="list-title">{t('DASHBOARD.UPCOMING_EVENT')}</h3>
                            </div>
                            <div className="text-center py-8 text-gray-400 text-sm">
                                {t('DASHBOARD.NO_UPCOMING_EVENT')}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
